import React, { useCallback, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  ControllerCalendar,
  ControllerDropdownInput,
  ControllerMultiSelect,
  ControllerTextInput,
} from "components/basic/controller-inputs/ControllerInputs";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import {
  MaintenanceDto,
  MaintenanceStatus,
  MdmObjectDto,
  useCreateMaintenanceMutation,
  useDeleteMaintenanceMutation,
  useGetObjectsByProjectQuery,
  useTriggerLastEmailsLazyQuery,
  useUpdateMaintenanceMutation,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "primereact/selectitem";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import { ChronoUnit, Instant } from "@js-joda/core";
import useProjectContext from "../../hooks/useProjectContext";
import { maintenanceStatuses } from "../../utils/const";
import { statuses } from "../../utils/types";
import { apolloErrorToaster, renderTag, successToaster } from "../../utils/utils";

type MaintenanceFormProps = {
  maintenance?: MaintenanceDto;
  closeDialog?: () => void;
  newForm?: boolean;
};

const schema = z.object({
  title: z.string({ message: T("word.required-fields") }),
  status: z.enum(statuses),
  reason: z.string({ message: T("word.required-fields") }),
  interruptedServices: z.string({ message: T("word.required-fields") }),
  submissionTime: z.date({ message: T("word.required-fields") }).or(z.string()),
  approvalTime: z.nullable(z.date()).or(z.optional(z.string())),
  disconnectionTime: z.nullable(z.date()).or(z.optional(z.string())),
  reconnectionTime: z.nullable(z.date()).or(z.optional(z.string())),
  executionTime: z.nullable(z.date()).or(z.optional(z.string())),
  objectIds: z.array(z.string(), { message: T("word.required-fields") }),
  comment: z.nullable(z.optional(z.string())),
}).superRefine((data, ctx) => {
  const { status, approvalTime, disconnectionTime, reconnectionTime } = data;

  if (status === MaintenanceStatus.Planned && !approvalTime) {
    ctx.addIssue({
      path: ["approvalTime"],
      code: z.ZodIssueCode.custom,
      message: T("word.required-fields"),
    });
  }
  
  if (status === MaintenanceStatus.InProgress && !disconnectionTime) {
    ctx.addIssue({
      path: ["disconnectionTime"],
      code: z.ZodIssueCode.custom,
      message: T("word.required-fields"),
    });
  }

  if (status === MaintenanceStatus.Done && !reconnectionTime) {
    ctx.addIssue({
      path: ["reconnectionTime"],
      code: z.ZodIssueCode.custom,
      message: T("word.required-fields"),
    });
  }
});

const FILTER_FIELD = "boiler";

const MaintenanceForm: React.FC<MaintenanceFormProps> = ({ maintenance, closeDialog, newForm = false }) => {
  const [selectedBoiler, setSelectedBoiler] = useState<string | null>(null);
  const [objects, setObjects] = useState<SelectItem[]>([]);
  const navigate = useNavigate();
  const { projectId, project } = useProjectContext();
  const { data, loading } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
    onError: apolloErrorToaster,
  });

  const [createMaintenanceMutation, { loading: createLoading }] = useCreateMaintenanceMutation({
    onCompleted: () => {
      successToaster(T("alert.maintenance-created-successfully"));
      closeDialog?.();
    },
    onError: apolloErrorToaster,
    refetchQueries: ["GetMaintenanceByProjectId"],
  });

  const [updateMaintenanceMutation, { loading: updateLoading }] = useUpdateMaintenanceMutation({
    onCompleted: (resp) => {
      successToaster(T("alert.maintenance-updated-successfully"));
      setValue("executionTime", resp.updateMaintenance.executionTime)
    },
    onError: apolloErrorToaster,
  });

  const [deleteMaintenanceMutation, { loading: deleteLoading }] = useDeleteMaintenanceMutation({
    onCompleted: () => {
      successToaster(T("alert.maintenance-deleted-successfully"));
      navigate(`/mdm/${projectId}/maintenance`);
    },
    onError: apolloErrorToaster,
    refetchQueries: ["GetMaintenanceByProjectId"],
  });

  const [triggerLastEmails, { loading: emailsLoading }] = useTriggerLastEmailsLazyQuery({
    onCompleted: () => {
      successToaster(T("alert.maintenance-updated-successfully"));
    },
    onError: apolloErrorToaster,
    fetchPolicy: "no-cache",
  });

  const {
    control,
    handleSubmit,
    resetField,
    setValue,
    formState: { errors },
  } = useForm<MaintenanceDto>({
    defaultValues: maintenance,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (maintenance && data?.mdmObjectsByProjectId) {
      const boilers = data.mdmObjectsByProjectId.reduce((acc, obj) => {
        if (maintenance.objectIds?.includes(obj.id!)) {
          const boiler = obj.customFields?.find(cf => cf?.key === FILTER_FIELD)?.value?.value;
          boiler && acc.push(boiler);
        }
        return acc;
      }, [] as string[]);
      const uniqBoilers = [...new Set(boilers)];
      setSelectedBoiler(uniqBoilers.length === 1 ? uniqBoilers[0] : null);
    }
  }, [data?.mdmObjectsByProjectId, maintenance]);

  const formatObjects = (mdmObjects: MdmObjectDto[]) =>
    mdmObjects
      .map(obj => ({
        label: `${obj.address?.fullAddress} - ${obj.title} - ${obj.identifier}`,
        value: obj.id,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label)) || [];

  useEffect(() => {
    if (data?.mdmObjectsByProjectId) {
      const formatted = formatObjects(data?.mdmObjectsByProjectId as MdmObjectDto[]);
      setObjects(formatted);
    }
  }, [data]);

  const onSubmit = (formData: MaintenanceDto) => {
    if (newForm) {
      createMaintenanceMutation({
        variables: {
          input: {
            ...formData,
            projectId,
          },
        },
      });
    } else {
      const executionTime =
        formData.status === MaintenanceStatus.Done
          ? Instant.now().truncatedTo(ChronoUnit.HOURS)
          : null;

      updateMaintenanceMutation({
        variables: {
          input: {
            ...maintenance,
            ...formData,
            executionTime: formData.executionTime ?? executionTime,
            projectId,
          },
        },
      });
    }
  };

  const handleSendEmails = () => {
    if (maintenance && !newForm) {
      triggerLastEmails({ variables: { maintenance } });
    }
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteMaintenanceMutation({
          variables: {
            id: maintenance?.id!,
          },
        });
      },
    });
  };

  const filterObjectsByBoiler = useCallback(
    (boiler: string) => {
      if (data?.mdmObjectsByProjectId) {
        if (boiler === "-") {
          return data?.mdmObjectsByProjectId;
        }
        if (boiler === T("mdm.without-boiler")) {
          return data?.mdmObjectsByProjectId.filter(
            obj => !obj.customFields?.find(cf => cf?.key === FILTER_FIELD)
          );
        }
        return data?.mdmObjectsByProjectId.filter(
          obj => obj.customFields?.find(cf => cf?.key === FILTER_FIELD)?.value?.value === boiler
        );
      }
      return [];
    },
    [data?.mdmObjectsByProjectId]
  );

  useEffect(() => {
    const filtered = filterObjectsByBoiler(selectedBoiler ?? "-");
    const formatted = formatObjects(filtered as MdmObjectDto[]);
    setObjects(formatted);
  }, [filterObjectsByBoiler, selectedBoiler]);

  const boilerCodes = data?.mdmObjectsByProjectId.map(
    obj =>
      obj.customFields?.find(cf => cf?.key === FILTER_FIELD)?.value?.value ??
      T("mdm.without-boiler")
  );
  const uniqBoilers = Array.from(new Set(boilerCodes))?.sort((a, b) => a.localeCompare(b));

  return (
    <form className="maintenance-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={12} />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="status"
          defaultValue={MaintenanceStatus.Registered}
          control={control}
          render={({ field }) => (
            <ControllerDropdownInput
              field={field}
              errors={errors}
              label={T("word.status")}
              options={maintenanceStatuses}
              optionLabel="translationKey"
              itemTemplate={opt => renderTag({ status: opt.value })}
              valueTemplate={opt => opt && renderTag({ status: opt.value })}
              col={2}
              className="status-dropdown"
            />
          )}
        />
        <Controller
          name="interruptedServices"
          control={control}
          render={({ field }) => (
            <ControllerDropdownInput
              field={field}
              errors={errors}
              label={T("word.interrupted-services")}
              options={
                project?.mdmProjectById.configuration?.maintenanceConfiguration
                  .interruptedServices ?? []
              }
              optionLabel="title"
              optionValue="id"
              col={3}
            />
          )}
        />
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.reason")} col={7} />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="submissionTime"
          defaultValue={new Date()}
          control={control}
          render={({ field }) => (
            <ControllerCalendar
              field={field}
              errors={errors}
              label={T("word.submission-time")}
              className="mt-auto"
              col={2}
            />
          )}
        />
        <Controller
          name="approvalTime"
          control={control}
          render={({ field }) => (
            <ControllerCalendar
              field={field}
              errors={errors}
              label={T("word.approval-time")}
              className="mt-auto"
              col={2}
            />
          )}
        />
        <Controller
          name="disconnectionTime"
          control={control}
          render={({ field }) => (
            <ControllerCalendar
              field={field}
              errors={errors}
              label={T("word.disconnection-time")}
              className="mt-auto"
              col={3}
            />
          )}
        />
        <Controller
          name="reconnectionTime"
          control={control}
          render={({ field }) => (
            <ControllerCalendar
              field={field}
              errors={errors}
              label={T("word.reconnection-time")}
              className="mt-auto"
              col={3}
            />
          )}
        />
        <Controller
          name="executionTime"
          control={control}
          render={({ field }) => (
            <ControllerCalendar
              field={field}
              errors={errors}
              label={T("word.execution-time")}
              stepMinute={60}
              className="mt-auto"
              col={2}
            />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          control={control}
          name="objectIds"
          render={({ field }) => (
            <ControllerMultiSelect
              field={field}
              errors={errors}
              label={T("mdm.objects")}
              placeholder={T("select.objects")}
              options={objects}
              virtualScrollerOptions={{ itemSize: 40 }}
              maxSelectedLabels={0}
              selectedItemsLabel={`${T("mdm.selected-objects")}: {0}`}
              filter
              loading={loading}
              multiple
            />
          )}
        />
        <InputLabelWrapper labelName={T("mdm.filter-by-boiler")}>
          <Dropdown
            value={selectedBoiler}
            options={["-", ...uniqBoilers]}
            onChange={e => {
              setSelectedBoiler(e.value);
              resetField("objectIds");
            }}
            className="p-inputtext-sm"
            placeholder={T("word.select-one")}
            loading={loading}
          />
        </InputLabelWrapper>
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.comment")} col={12} />
          )}
        />
      </div>
      {newForm && (
        <div className="flex flex-wrap gap-2 justify-content-end">
          <Button
            type="submit"
            label={T("action.create")}
            loading={createLoading}
            className="p-button-rounded p-button-success w-10rem"
          />
          <Button
            type="button"
            onClick={closeDialog}
            label={T("word.cancel")}
            rounded
            severity="secondary"
            outlined
            className="w-10rem"
          />
        </div>
      )}
      {!newForm && (
        <div className="flex flex-wrap gap-2 justify-content-between px-2">
          <Button
            type="button"
            title={T("action.send-email")}
            icon="pi pi-envelope"
            loading={emailsLoading}
            outlined
            onClick={handleSendEmails}
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              label={T("action.update")}
              loading={updateLoading}
              className="p-button-rounded p-button-primary w-10rem"
            />
            <Button
              type="button"
              label={T("action.delete")}
              className="p-button-rounded p-button-danger w-10rem"
              onClick={handleDelete}
              loading={deleteLoading}
            />
            <Button
              type="button"
              onClick={() => navigate(-1)}
              label={T("action.back")}
              rounded
              severity="secondary"
              outlined
              className="w-10rem"
            />
          </div>
        </div>
      )}
    </form>
  );
};

export default MaintenanceForm;
